import React, { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { templateGalleryMessages } from '@atlaskit/editor-common/messages';
import {
	QuickInsertMenuItem,
	type OnSelectContext,
} from '@atlaskit/editor-common/quick-insert/menu-item';
import { messages as quickInsertMessages } from '@atlaskit/editor-common/quick-insert/messages';
import { useQuickInsertContext } from '@atlaskit/editor-common/quick-insert/use-quick-insert-context';
import TemplateIcon from '@atlaskit/icon-lab/core/template';
import Lozenge from '@atlaskit/lozenge/new/lozenge';

import { openTemplateGallerySafely } from '../../utils/openTemplateGallery';

const previewImageUrls = {
	light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OYIT.png',
	dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OYIU.png',
};

type Props = {
	onOpenTemplateGallery: () => void;
};

export const TemplateGalleryQuickInsertMenuItem = ({
	onOpenTemplateGallery,
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { isOffline } = useQuickInsertContext();
	const title = formatMessage(templateGalleryMessages.templateGalleryTitle);
	const preview = useMemo(
		() => ({
			image: previewImageUrls,
			attribution: { name: formatMessage(quickInsertMessages.previewAttributionAtlassian) },
		}),
		[formatMessage],
	);
	const onSelect = useCallback(
		({ editorView }: OnSelectContext) => {
			openTemplateGallerySafely(onOpenTemplateGallery);
			return editorView.state.tr;
		},
		[onOpenTemplateGallery],
	);

	return (
		<QuickInsertMenuItem
			description={formatMessage(templateGalleryMessages.templateGalleryDescription)}
			iconBefore={<TemplateIcon label={title} />}
			isDisabled={isOffline}
			lozenge={
				<Lozenge appearance="new">{formatMessage(templateGalleryMessages.newLozenge)}</Lozenge>
			}
			onSelect={onSelect}
			preview={preview}
			title={title}
		/>
	);
};
